// Prompt templates for OpenAI content generation
// Based on implementation plan Phase 5.2

interface PromptParams {
  lexileLevel: string;
  genre: string;
  topic: string;
  paragraphCount: number;
  standard: string;
  storyTitle?: string;
  storyPreview?: string;
  duration?: string;
}

/**
 * Generate passage prompt based on section type
 */
export const buildStoryPrompt = (params: PromptParams, section: 'introduction' | 'independent'): string => {
  const basePrompt = `Create an engaging ${params.lexileLevel} reading level passage for small group reading instruction.

Requirements:
- Reading level: ${params.lexileLevel}
- Genre: ${params.genre}
- Topic: ${params.topic}
- Paragraphs: exactly ${params.paragraphCount} paragraphs
- Learning standard: ${params.standard}
- Duration: 5-7 minutes of reading time

The passage should:
- Have a clear title
- Include a beginning, middle, and end
- Be engaging and age-appropriate
- Support the learning standard with relevant vocabulary and concepts
- Be formatted with proper paragraph breaks`;

  if (section === 'independent') {
    return basePrompt + `
- Must be completely different from any introduction passage
- Provide additional practice opportunities for the same learning objectives
- Include different characters, setting, or scenario while maintaining the same topic theme

Format the response as:
Title: [Passage Title]

[Paragraph 1]

[Paragraph 2]

[Continue for all paragraphs...]`;
  }

  return basePrompt + `

Format the response as:
Title: [Passage Title]

[Paragraph 1]

[Paragraph 2]

[Continue for all paragraphs...]`;
};

/**
 * Generate lesson introduction activities prompt
 */
export const buildIntroductionActivitiesPrompt = (params: PromptParams): string => {
  return `Create lesson introduction activities for a 10-15 minute small group session.

Context:
- Passage: ${params.storyTitle || 'the passage we just created'}
- Learning standard: ${params.standard}
- Student level: ${params.lexileLevel}
- Genre: ${params.genre}
- Topic: ${params.topic}

Create these pre-reading activities:

1. **Pre-Reading Discussion** (2-3 minutes)
   - 2-3 engaging questions to activate prior knowledge
   - Connect to students' experiences

2. **Vocabulary Preview** (3-4 minutes)
   - Introduce 3-4 key vocabulary words from the passage
   - Use student-friendly definitions
   - Include quick practice or examples

3. **Reading Strategy Introduction** (2-3 minutes)
   - Introduce one reading strategy relevant to the standard
   - Explain how students will use it while reading

4. **Purpose Setting** (1-2 minutes)
   - Clear purpose for reading
   - Questions to think about while reading

Format with clear headings and time allocations. Make activities interactive and engaging for small group instruction.`;
};

/**
 * Generate independent practice activities prompt
 */
export const buildPracticeActivitiesPrompt = (params: PromptParams): string => {
  return `Create independent practice activities based on the story and learning standard.

Context:
- Story: ${params.storyTitle || 'the practice story'}
- Learning standard: ${params.standard}
- Student level: ${params.lexileLevel}
- Genre: ${params.genre}
- Topic: ${params.topic}

Create these post-reading activities:

1. **Comprehension Questions** (5-7 minutes)
   - 3-4 multiple choice questions
   - 2-3 short answer questions
   - Questions should target the learning standard

2. **Vocabulary Practice** (3-5 minutes)
   - Activities using vocabulary from the story
   - Could include matching, fill-in-blank, or context clues

3. **Standard-Specific Practice** (5-7 minutes)
   - Activities that directly practice the learning standard
   - Hands-on or interactive when possible

4. **Extension Activities** (3-5 minutes)
   - For early finishers
   - Connect to real-world applications

Format as student-ready activities with clear directions. Include answer keys where appropriate.`;
};

/**
 * Parse story content from OpenAI response
 */
export const parseStoryContent = (content: string): { title: string; content: string } => {
  // CRITICAL FIX: Ensure content is always a string to prevent {object Object} issues
  const safeContent = typeof content === 'string' ? content : String(content);
  const lines = safeContent.trim().split('\n').filter(line => line.trim());
  
  let title = 'Untitled Story';
  let storyContent = '';
  
  // Helper function to clean title of markdown/markup characters
  const cleanTitle = (rawTitle: string): string => {
    return rawTitle
      .replace(/^\*\*|\*\*$/g, '') // Remove ** markdown bold
      .replace(/^#+\s*/, '') // Remove # markdown headers
      .replace(/^Title:\s*/, '') // Remove "Title:" prefix
      .replace(/[*_`]/g, '') // Remove other markdown characters
      .trim();
  };
  
  // Look for title pattern (with various possible formats)
  const titleMatch = safeContent.match(/^(?:Title:\s*|\*\*|#+\s*)?(.+?)(?:\*\*)?$/m);
  if (titleMatch) {
    title = cleanTitle(titleMatch[1]);
    // Remove title line from content (be more flexible with the pattern)
    storyContent = safeContent.replace(/^(?:Title:\s*|\*\*|#+\s*).+?(?:\*\*)?$/m, '').trim();
  } else {
    // If no title found, use first line as title
    if (lines.length > 0) {
      title = cleanTitle(lines[0]);
      storyContent = lines.slice(1).join('\n').trim();
    } else {
      storyContent = safeContent;
    }
  }
  
  // Clean up any remaining markdown from story content
  storyContent = storyContent
    .replace(/^\*\*|\*\*$/gm, '') // Remove bold markdown
    .replace(/^#+\s*/gm, '') // Remove header markdown
    .trim();
  
  return { title, content: storyContent };
};

/**
 * Calculate reading time and word count
 */
export const calculateStoryMetadata = (content: string) => {
  const words = content.trim().split(/\s+/).length;
  const readingTimeMinutes = Math.max(1, Math.round(words / 150)); // Average reading speed
  
  return {
    wordCount: words,
    readingTimeMinutes
  };
}; 